import { requestV2 } from '@ltvco/refresh-lib/utils';
import {
  CreateCommentParams,
  SocialSentiment,
  SocialSentimentComment,
  SocialSentimentsParams,
  SocialSentimentsResponse,
} from './interfaces';
import { normalizeYear } from 'utils/normalizeYear';
import {
  DEFAULT_MAX_POLL_RETRIES,
  DEFAULT_POLL_RETRY_DELAY_MS,
  SOCIAL_SENTIMENTS_BASE_URL,
} from '../communityBuzz/constants';

const getSocialSentimentsHeaders = (token?: string) => ({
  'content-type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

class HttpError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.payload = payload;
  }
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (!signal) {
      setTimeout(resolve, ms);
      return;
    }

    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort);
  });

export const getSocialSentiments = async (
  params: SocialSentimentsParams,
  token?: string,
  maxRetries: number = DEFAULT_MAX_POLL_RETRIES,
  retryDelay: number = DEFAULT_POLL_RETRY_DELAY_MS,
  signal?: AbortSignal,
): Promise<SocialSentimentsResponse> => {
  const searchParams = new URLSearchParams();
  if (params.year !== undefined) {
    searchParams.append(
      'year',
      normalizeYear(params.year, 'social sentiments'),
    );
  }
  if (params.make !== undefined) {
    searchParams.append('make', params.make);
  }
  if (params.model !== undefined) {
    searchParams.append('model', params.model);
  }
  if (params.trim !== undefined) {
    searchParams.append('trim', params.trim);
  }

  const url = `${SOCIAL_SENTIMENTS_BASE_URL}s?${searchParams.toString()}`;
  const init = {
    headers: getSocialSentimentsHeaders(token),
    method: 'GET',
  };

  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      // Make the request using fetch to check status code
      const response = await fetch(url, { ...init, signal });
      const status = response.status;

      // If status is 201 or 202, continue polling
      if (status === 201 || status === 202) {
        retryCount++;

        if (retryCount >= maxRetries) {
          throw new Error(
            `Polling exceeded maximum retries (${maxRetries}) for social sentiments`,
          );
        }

        await sleep(retryDelay, signal);
        continue;
      }

      // For other non-success status codes, parse error and throw
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new HttpError(
          `Request failed with status ${status}: ${JSON.stringify(errorData)}`,
          status,
          errorData,
        );
      }

      // Success (200) - parse and return the data
      const data = await response.json().catch(() => ({}));
      // Ensure the response has the required fields for SocialSentimentsResponse
      const safeResponse: SocialSentimentsResponse = {
        items: Array.isArray(data.items) ? data.items : [],
        page: typeof data.page === 'number' ? data.page : 1,
        page_size: typeof data.page_size === 'number' ? data.page_size : 0,
        total: typeof data.total === 'number' ? data.total : 0,
      };
      return safeResponse;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }

      if (signal?.aborted) {
        throw error;
      }

      // Network errors (fetch throws) - retry if we haven't exceeded max retries
      // HTTP errors are handled above and won't reach here
      retryCount++;
      if (retryCount >= maxRetries) {
        throw error;
      }
      await sleep(retryDelay, signal);
      // Continue to retry
    }
  }

  // If we've exhausted retries, throw an error
  throw new Error(
    `Polling exceeded maximum retries (${maxRetries}) for social sentiments`,
  );
};

export const getSocialSentiment = async (
  id: number,
  token?: string,
): Promise<SocialSentiment> => {
  const init = {
    headers: getSocialSentimentsHeaders(token),
    method: 'GET',
  };

  return await requestV2({
    url: `${SOCIAL_SENTIMENTS_BASE_URL}/${id}`,
    options: init,
  });
};

export const createComment = async (
  params: CreateCommentParams,
  token?: string,
): Promise<SocialSentimentComment> => {
  const init = {
    headers: getSocialSentimentsHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return await requestV2({
    url: `${SOCIAL_SENTIMENTS_BASE_URL}/${params.social_sentiment_id}/comment`,
    options: init,
  });
};
